/**
 * Auth Slice
 * Manages authentication state
 */

import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { storefrontAPI } from '../../utils/api';
import { AuthResponse, AuthState, LoginCredentials, LoginResponse, SessionData } from '../types';

// Initial State
export const authInitialState: AuthState = {
    isAuthenticated: false,
    isLoading: false,
    error: null,
    sessionId: null,
    sessionToken: null,
};

// Async Thunks

/**
 * Sign in with access token and pin
 */
export const signIn = createAsyncThunk<
    LoginResponse,
    LoginCredentials,
    { state: { auth: AuthState }; rejectValue: string }
>('auth/signIn', async ({ accessToken, pin }, { rejectWithValue }) => {
    try {
        const response = await storefrontAPI.login(accessToken, pin);
        if (response.error) {
            return rejectWithValue(response.message || 'Invalid credentials. Please try again.');
        }
        return response;
    } catch {
        return rejectWithValue('Invalid credentials. Please try again.');
    }
});

/**
 * Authenticate existing session
 */
export const authenticate = createAsyncThunk<
    AuthResponse,
    SessionData,
    { state: { auth: AuthState }; rejectValue: string }
>('auth/authenticate', async ({ session_id, session_token }, { rejectWithValue }) => {
    try {
        const response = await storefrontAPI.authenticate(session_id, session_token);
        if (!response.user) {
            return rejectWithValue('Session expired');
        }
        return response;
    } catch {
        return rejectWithValue('Authentication failed');
    }
});

/**
 * Request PIN reset
 */
export const requestPinReset = createAsyncThunk<void, string, { rejectValue: string }>(
    'auth/requestPinReset',
    async (accessToken, { rejectWithValue }) => {
        try {
            const response = await storefrontAPI.resetPin(accessToken);

            if (response && typeof response === 'object' && 'error' in response && response.error) {
                const errorMessage = 'message' in response ? String(response.message) : 'Reset request failed';
                return rejectWithValue(errorMessage);
            }

            return;
        } catch {
            return rejectWithValue('Failed to send reset request');
        }
    },
);

// Slice
export const authSlice = createSlice({
    name: 'auth',
    initialState: authInitialState,
    reducers: {
        setAuthenticated(state, action: PayloadAction<boolean>) {
            state.isAuthenticated = action.payload;
        },
        setAuthError(state, action: PayloadAction<string | null>) {
            state.error = action.payload;
        },
        clearAuthError(state) {
            state.error = null;
        },
        setAuthLoading(state, action: PayloadAction<boolean>) {
            state.isLoading = action.payload;
        },
        logout(state) {
            state.isAuthenticated = false;
            state.sessionId = null;
            state.sessionToken = null;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Sign In
        builder
            .addCase(signIn.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(signIn.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isAuthenticated = true;
                state.sessionId = action.payload.session_id;
                state.sessionToken = action.payload.session_token;
            })
            .addCase(signIn.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });

        // Authenticate
        builder
            .addCase(authenticate.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(authenticate.fulfilled, (state) => {
                state.isLoading = false;
                state.isAuthenticated = true;
            })
            .addCase(authenticate.rejected, (state) => {
                state.isLoading = false;
                state.isAuthenticated = false;
            });
    },
});

// Export actions
export const { setAuthenticated, setAuthError, clearAuthError, setAuthLoading, logout } = authSlice.actions;

// Selectors
type RootState = { auth: AuthState };

export const selectAuth = (state: RootState): AuthState => state.auth;
export const selectIsAuthenticated = createSelector(selectAuth, (auth) => auth.isAuthenticated);
export const selectIsAuthLoading = createSelector(selectAuth, (auth) => auth.isLoading);
export const selectAuthError = createSelector(selectAuth, (auth) => auth.error);
export const selectSessionData = createSelector(selectAuth, (auth) => ({
    session_id: auth.sessionId,
    session_token: auth.sessionToken,
}));

export default authSlice.reducer;
